import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import {
  AccountInfo,
  EventMessage,
  EventType,
  InteractionType,
  InteractionRequiredAuthError,
  InteractionStatus
} from '@azure/msal-browser';
import { filter, map, Observable, Subject, take, takeUntil } from 'rxjs';
import { UserService } from './modules/shared/services/user.service';
import { environment } from 'src/environments/environment';
import { MANUAL_LOGOUT_STORAGE_KEY } from './authentication.constants';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'component.admin-portal';

  isAuthorized: Observable<boolean>;
  isSignedOut = false;
  authenticationError: string = null;

  private readonly destroying$ = new Subject<void>();

  constructor(
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private userService: UserService,
    private changeDetectorRef: ChangeDetectorRef) {
    this.isAuthorized = userService.getUser().pipe(map(u => !!u));
  }

  ngOnInit(): void {
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(event => event.eventType === EventType.ACQUIRE_TOKEN_FAILURE
          && event.interactionType === InteractionType.Redirect),
        takeUntil(this.destroying$)
      )
      .subscribe(event => this.handleAuthenticationFailure(event));

    // AuthenticationRedirectComponent handles the response from AAD. Wait until that
    // processing is complete before reading accounts or starting a new login.
    this.msalBroadcastService.inProgress$
      .pipe(
        filter(status => status === InteractionStatus.None),
        take(1),
        takeUntil(this.destroying$)
      )
      .subscribe(() => this.authenticate());
  }

  ngOnDestroy(): void {
    this.destroying$.next();
    this.destroying$.complete();
  }

  private authenticate(): void {
    const account = this.authService.instance.getActiveAccount()
      ?? this.authService.instance.getAllAccounts()[0];

    if (!account) {
      if (this.authenticationError) {
        return;
      }

      if (sessionStorage.getItem(MANUAL_LOGOUT_STORAGE_KEY)) {
        this.isSignedOut = true;
        this.changeDetectorRef.detectChanges();
        return;
      }

      this.login();
      return;
    }

    this.authService.instance.setActiveAccount(account);
    this.acquireToken(account);
  }

  private acquireToken(account: AccountInfo): void {
    this.authService.acquireTokenSilent({ account, scopes: [environment.authScope] })
      .pipe(take(1), takeUntil(this.destroying$))
      .subscribe({
        next: result => this.userService.setUser(result.account),
        error: error => {
          if (error instanceof InteractionRequiredAuthError) {
            this.login();
            return;
          }

          this.setAuthenticationError(error);
        }
      });
  }

  login(): void {
    sessionStorage.removeItem(MANUAL_LOGOUT_STORAGE_KEY);
    this.isSignedOut = false;
    this.authenticationError = null;

    this.authService.loginRedirect({ scopes: [environment.authScope] })
      .pipe(take(1), takeUntil(this.destroying$))
      .subscribe({
        error: error => this.setAuthenticationError(error)
      });
  }

  private handleAuthenticationFailure(event: EventMessage): void {
    this.setAuthenticationError(event.error);
  }

  private setAuthenticationError(error: unknown): void {
    this.authenticationError = 'Sign-in could not be completed. Please try again.';
    this.changeDetectorRef.detectChanges();
    console.error('AAD authentication failed.', error);
  }
}
